import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/providers/auth-provider";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, EyeOff, User, Lock, Loader2, Mail, Smartphone } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import logoImage from "@assets/ZentroBooksLogo.png";

const loginSchema = z.object({
  email: z.string().min(1, { message: "Email is required" }).email({ message: "Please enter a valid email address" }),
  password: z.string().min(1, { message: "Password is required" }),
});

const twoFactorSchema = z.object({
  code: z.string().min(6, { message: "Code must be 6 digits" }).max(6, { message: "Code must be 6 digits" }),
});

type LoginFormValues = z.infer<typeof loginSchema>;
type TwoFactorFormValues = z.infer<typeof twoFactorSchema>;

export default function LoginPage() {
  const { user, isLoading, login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  
  // 2FA state management
  const [loginStep, setLoginStep] = useState<"credentials" | "twoFactor">("credentials");
  const [tempUser, setTempUser] = useState<any>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);

  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [credentialsError, setCredentialsError] = useState("");

  // Handle URL params (verification success, errors)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("verified") === "true") {
      toast({ title: "Email verified!", description: "You can now log in to your account." });
      window.history.replaceState({}, "", "/login");
    }
    if (params.get("error") === "invalid_token") {
      toast({ title: "Invalid or expired link", description: "Please request a new verification email.", variant: "destructive" });
      window.history.replaceState({}, "", "/login");
    }
    if (params.get("error") === "token_expired") {
      toast({ title: "Link expired", description: "Please request a new verification email.", variant: "destructive" });
      window.history.replaceState({}, "", "/login");
    }
  }, []);

  // Redirect if already logged in — role-based
  useEffect(() => {
    if (user) {
      const admin = user.email === "hello@zentrobooks.com";
      console.log(`[LOGIN] Redirect: user=${user.email} isPlatformAdmin=${admin}`);
      navigate(admin ? "/admin/dashboard" : "/dashboard");
    }
  }, [user, navigate]);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const twoFactorForm = useForm<TwoFactorFormValues>({
    resolver: zodResolver(twoFactorSchema),
    defaultValues: {
      code: "",
    },
  });
  
  // First step: validate credentials and initiate 2FA
  const onSubmit = async (values: LoginFormValues) => {
    console.log("LoginPage: Submitting login form with email:", values.email);
    setIsLoggingIn(true);
    setCredentialsError(""); // Clear previous inline error
    try {
      // First authenticate with username/password
      const response = await apiRequest("POST", "/api/auth/login", {
        email: values.email,
        password: values.password
      });

      if (response.ok) {
        const data = await response.json();

        // Check if user has 2FA enabled
        if (data.requiresTwoFactor) {
          setTempUser(data.user);
          setLoginStep("twoFactor");

          // Send email verification code - use the same credentials to maintain session
          setIsSendingCode(true);
          const smsResponse = await apiRequest("POST", "/api/auth/2fa/login", {
            email: values.email,
            password: values.password
          });

          if (smsResponse.ok) {
            toast({
              title: "Verification Code Sent",
              description: "Please check your email for the 6-digit verification code.",
            });
          }
          setIsSendingCode(false);
        } else {
          // Complete login if no 2FA required
          await login(values.email, values.password);
        }
      } else {
        const errorData = await response.json();

        // Handle email not verified
        if (errorData.code === "EMAIL_NOT_VERIFIED") {
          setEmailNotVerified(true);
          setUnverifiedEmail(errorData.email || values.email);
          setIsLoggingIn(false);
          return;
        }

        // 401: show inline error — no toast (clean UX, no info leakage)
        if (response.status === 401) {
          setCredentialsError("Email or password is incorrect");
        } else {
          // 500 or other server errors: show toast
          toast({
            title: "Login Failed",
            description: "Something went wrong. Please try again later.",
            variant: "destructive",
          });
        }
      }
    } catch (error: any) {
      console.error("LoginPage: Login error:", error);

      // apiRequest throws on non-ok responses — check status from the thrown error
      const status = error?.status || error?.response?.status;

      if (status === 401) {
        // Invalid credentials — inline message, no toast
        setCredentialsError("Email or password is incorrect");
      } else if (status === 403 && error?.details?.code === "EMAIL_NOT_VERIFIED") {
        // Email not verified — show banner
        setEmailNotVerified(true);
        setUnverifiedEmail(error?.details?.email || form.getValues("email"));
      } else if (status && status >= 400) {
        // Other server errors — toast
        toast({
          title: "Login Failed",
          description: "Something went wrong. Please try again later.",
          variant: "destructive",
        });
      } else {
        // Network error — toast
        toast({
          title: "Connection Error",
          description: "Unable to reach the server. Please check your connection and try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Second step: verify 2FA code
  const onTwoFactorSubmit = async (values: TwoFactorFormValues) => {
    setIsLoggingIn(true);
    try {
      if (!tempUser?.email) {
        console.error("Missing email for 2FA verification");
        toast({
          title: "Login Error",
          description: "Session information is missing. Please try logging in again.",
          variant: "destructive",
        });
        setLoginStep("credentials");
        return;
      }

      // Include the email from the first step to handle potential session loss
      const response = await apiRequest("POST", "/api/auth/2fa/verify-login", {
        code: values.code,
        email: tempUser.email // Include email to help with session recovery
      });

      if (response.ok) {
        // 2FA verification already completed the login process
        const result = await response.json();
        
        // Role-based redirect after 2FA
        const admin = tempUser?.email === "hello@zentrobooks.com";
        console.log(`[LOGIN] 2FA complete: user=${tempUser?.email} isPlatformAdmin=${admin}`);
        window.location.href = admin ? "/admin/dashboard" : "/dashboard";
        
        toast({
          title: "Login Successful",
          description: "Welcome back to ZentroBooks!",
        });
      } else {
        const errorData = await response.json();
        toast({
          title: "Verification Failed", 
          description: errorData.message || "Invalid verification code",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("2FA verification error:", error);
      toast({
        title: "Verification Failed",
        description: "An error occurred during verification. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoggingIn(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-4">
      <Card className="w-full max-w-md">
        {/* Email not verified banner */}
        {emailNotVerified && (
          <div className="bg-yellow-50 border-b border-yellow-200 p-4 rounded-t-lg text-center space-y-2">
            <p className="text-sm font-medium text-yellow-800">Please verify your email before logging in</p>
            <p className="text-xs text-yellow-600">Check <span className="font-semibold">{unverifiedEmail}</span> for the verification link</p>
            <button
              className="text-xs text-primary underline hover:no-underline"
              onClick={async () => {
                await fetch("/api/auth/resend-verification", {
                  method: "POST", headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ email: unverifiedEmail }),
                });
                toast({ title: "Verification email sent", description: `Check ${unverifiedEmail}` });
              }}
            >
              Resend verification email
            </button>
          </div>
        )}
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <img src={logoImage} alt="ZentroBooks Logo" className="h-16" />
          </div>
          <CardDescription className="text-center">
            {loginStep === "credentials" 
              ? "Log in to access your financial dashboard"
              : "Enter the verification code sent to your phone"
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loginStep === "credentials" ? (
            // Step 1: Username/Password Form
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 h-4 w-4" />
                          <Input
                            className="pl-9"
                            placeholder="your@email.com"
                            type="email"
                            autoComplete="email"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 h-4 w-4" />
                          <Input
                            className="pl-9 pr-10"
                            placeholder="******"
                            type={showPassword ? "text" : "password"}
                            autoComplete="current-password"
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4 text-neutral-400" />
                            ) : (
                              <Eye className="h-4 w-4 text-neutral-400" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <div className="flex justify-end">
                        <Link to="/auth/forgot-password" className="text-xs text-primary hover:underline">
                          Forgot password?
                        </Link>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {credentialsError && (
                  <p className="text-sm text-red-600 text-center mt-2">{credentialsError}</p>
                )}
                <Button className="w-full mt-6" type="submit" disabled={isLoggingIn}>
                  {isLoggingIn ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : "Continue"}
                </Button>
              </form>
            </Form>
          ) : (
            // Step 2: 2FA Verification Form
            <div className="space-y-4">
              <div className="text-center text-sm text-neutral-600 mb-4">
                {isSendingCode ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending verification code...
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <Mail className="h-4 w-4" />
                    Code sent to {tempUser?.email || "your email"}
                  </div>
                )}
              </div>
              
              <Form {...twoFactorForm}>
                <form onSubmit={twoFactorForm.handleSubmit(onTwoFactorSubmit)} className="space-y-4">
                  <FormField
                    control={twoFactorForm.control}
                    name="code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Verification Code</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="000000"
                            maxLength={6}
                            className="text-center text-lg tracking-widest"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <Button className="w-full mt-6" type="submit" disabled={isLoggingIn}>
                    {isLoggingIn ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Verifying...
                      </>
                    ) : "Verify & Log In"}
                  </Button>
                </form>
              </Form>
              
              <div className="text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setLoginStep("credentials")}
                  className="text-xs"
                >
                  ← Back to login
                </Button>
              </div>
            </div>
          )}
        </CardContent>
        {loginStep === "credentials" && (
          <CardFooter className="flex flex-col space-y-4">
            <div className="text-center text-sm">
              <span className="text-neutral-500">Don't have an account?</span>{" "}
              <Link to="/auth/signup" className="text-primary font-medium hover:underline">
                Sign up
              </Link>
            </div>
          </CardFooter>
        )}
      </Card>
    </div>
  );
}
